import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import L from 'leaflet';
import Fuse from 'fuse.js';
import 'leaflet/dist/leaflet.css';
import './App.css'; /* carries the two markercluster animation rules we need */

const PATH = process.env.PUBLIC_URL;

/* Data colors, mirroring the --chart-* tokens in index.css. Kept as literals
   because they are baked into generated SVG strings, not styled by cascade. */
const C = {
  magenta: '#c0338a',
  blue: '#2e6da4',
  slate: '#55677a',
  amber: '#e8a33d',
};

const AMENITIES = [
  { key: 'camping', label: 'Camping' },
  { key: 'courtesy_car', label: 'Courtesy car' },
  { key: 'bicycles', label: 'Bicycles' },
  { key: 'meals', label: 'Meals' },
];

const LAYERS = [
  { key: 'street', label: 'Street' },
  { key: 'topo', label: 'Topo' },
  { key: 'sectional', label: 'Sectional' },
];

const METERS_PER_NM = 1852;
const MAX_RADIUS_NM = 500;

/* ------------------------------------------------------------------ *
 * Sectional airfield symbols
 *
 * A circle with runway ticks is the symbol every pilot already reads on
 * a sectional. It replaces the teardrop pin: one 13-28px glyph instead of
 * a 25x41 marker plus a full airport-name label at every field.
 * ------------------------------------------------------------------ */

const TICKS = [
  'M12 1.5v3',
  'M12 19.5v3',
  'M1.5 12h3',
  'M19.5 12h3',
].join('');

function symbolSVG(variant) {
  const ticks = (color) =>
    `<path d="${TICKS}" stroke="${color}" stroke-width="2.4" stroke-linecap="round" fill="none"/>`;

  switch (variant) {
    case 'amenity':
      return `${ticks(C.magenta)}
        <circle cx="12" cy="12" r="7.2" fill="${C.magenta}" stroke="#fff" stroke-width="1.7"/>
        <circle cx="12" cy="12" r="2.1" fill="#fff"/>`;
    case 'directory':
      return `${ticks(C.magenta)}
        <circle cx="12" cy="12" r="6.6" fill="#fff" fill-opacity=".92" stroke="${C.magenta}" stroke-width="2.6"/>`;
    case 'towered':
      return `${ticks(C.blue)}
        <circle cx="12" cy="12" r="7.2" fill="${C.blue}" stroke="#fff" stroke-width="1.7"/>
        <circle cx="12" cy="12" r="2.1" fill="#fff"/>`;
    case 'seaplane':
      return `<circle cx="12" cy="12" r="6" fill="#fff" fill-opacity=".9" stroke="${C.slate}" stroke-width="2"/>
        <path d="M8.6 11h6.8M9.6 14h4.8" stroke="${C.slate}" stroke-width="1.6" stroke-linecap="round"/>`;
    default: // 'small'
      return `<circle cx="12" cy="12" r="5" fill="#fff" fill-opacity=".9" stroke="${C.slate}" stroke-width="2.2"/>`;
  }
}

const iconCache = new Map();

function airfieldIcon(variant, px, label) {
  const key = `${variant}|${px}|${label || ''}`;
  const hit = iconCache.get(key);
  if (hit) return hit;

  const text = label ? `<span class="sym-label mono">${label}</span>` : '';
  const icon = L.divIcon({
    className: 'airfield',
    html: `${text}<svg viewBox="0 0 24 24" width="${px}" height="${px}" aria-hidden="true">${symbolSVG(
      variant
    )}</svg>`,
    iconSize: [px, px],
    iconAnchor: [px / 2, px / 2],
  });
  iconCache.set(key, icon);
  return icon;
}

/* Selection is drawn as its own overlay marker rather than baked into the
   symbol icon — otherwise clicking a field re-renders all 19k markers. */
const selectionIcon = L.divIcon({
  className: 'lock-ring',
  html: '<span></span>',
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

const originIcon = L.divIcon({
  className: 'origin-mark',
  html: `<svg viewBox="0 0 32 32" width="32" height="32" aria-hidden="true">
    <circle cx="16" cy="16" r="11" fill="none" stroke="${C.amber}" stroke-width="1.6" opacity=".85"/>
    <circle cx="16" cy="16" r="4" fill="${C.amber}" stroke="#14181d" stroke-width="1.4"/>
    <path d="M16 1v6M16 25v6M1 16h6M25 16h6" stroke="${C.amber}" stroke-width="1.6" stroke-linecap="round"/>
  </svg>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

/* Zoom buckets keep icon churn to a handful of cached objects instead of one
   per marker per zoom event. Idents only appear once you're close enough that
   the labels can't collide. */
const SYM_PX = [13, 17, 22, 28];
const LABEL_FROM_BUCKET = 2;

function zoomBucket(z) {
  if (z >= 11) return 3;
  if (z >= 9) return 2;
  if (z >= 7) return 1;
  return 0;
}

function directoryVariant(item) {
  return AMENITIES.some((a) => item[a.key]) ? 'amenity' : 'directory';
}

function baseVariant(airport) {
  if (airport.type === 'seaplane_base') return 'seaplane';
  if (airport.type === 'medium_airport' || airport.type === 'large_airport') return 'towered';
  return 'small';
}

/* ------------------------------------------------------------------ */

function nmBetween(a, b) {
  const R = 3440.065; // Earth radius in nautical miles
  const rad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * rad;
  const dLon = (b[1] - a[1]) * rad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

const fmt = (n) => n.toLocaleString('en-US');

function faaDiagramUrl(airportId, diagrams) {
  const ident =
    airportId.length === 4 && airportId.startsWith('K') ? airportId.substring(1) : airportId;
  const pdf = diagrams[ident];
  if (!pdf) return null;
  const now = new Date();
  const yymm = `${now.getFullYear().toString().slice(2)}${String(now.getMonth() + 1).padStart(2, '0')}`;
  return `https://aeronav.faa.gov/d-tpp/${yymm}/${pdf}`;
}

/* ------------------------------------------------------------------ *
 * Amenity glyphs — drawn, not emoji, so they sit on the same grid as
 * everything else in the panel.
 * ------------------------------------------------------------------ */

function Glyph({ name }) {
  const p = {
    camping: 'M2 13.5 8 2.5l6 11M8 2.5v11M4.6 13.5h6.8',
    courtesy_car: 'M2 10.5h12M3.2 10.5 4.6 6h6.8l1.4 4.5M3.2 10.5v2M12.8 10.5v2M5 8.6h6',
    bicycles: 'M4 12.2a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8ZM12 12.2a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8ZM4 9.8 7 4.4h3M7 9.8h5',
    meals: 'M4 2.5v11M4 2.5v4M2.4 2.5v4M5.6 2.5v4M11.6 2.5c1.4 1 1.8 3.2 1 5h-1v6',
  }[name];
  return (
    <svg className="glyph" viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <path d={p} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const Chevron = ({ dir = 'down' }) => (
  <svg className={`chev chev-${dir}`} viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
    <path d="M3.5 6 8 10.5 12.5 6" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ------------------------------------------------------------------ *
 * Scanned directory page viewer — pan and zoom, because these pages are
 * the whole reason the app exists and you actually need to read them.
 * ------------------------------------------------------------------ */

function ScanViewer({ ident, name, onClose }) {
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const drag = useRef(new Map()); // active pointers, by id
  const pinch = useRef(null);
  const frame = useRef(null);

  /* Pointer handlers need the current transform without re-binding on it. */
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => setView({ k: 1, x: 0, y: 0 }), [ident]);

  const clampK = (k) => Math.min(6, Math.max(1, k));

  /* React registers onWheel passively at the root, so preventDefault there is
     a no-op. Bind it natively to keep the page from scrolling under the zoom. */
  useEffect(() => {
    const el = frame.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      setView((v) => {
        const k = Math.min(6, Math.max(1, v.k * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
        const r = k / v.k;
        return { k, x: cx - (cx - v.x) * r, y: cy - (cy - v.y) * r };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  /* One pointer pans, two pinch. The frame sets touch-action:none so the
     browser's own pinch never fires here — we have to provide it. */
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (drag.current.size === 2) {
      const [a, b] = [...drag.current.values()];
      const rect = frame.current.getBoundingClientRect();
      pinch.current = {
        d: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        cx: (a.x + b.x) / 2 - rect.left - rect.width / 2,
        cy: (a.y + b.y) / 2 - rect.top - rect.height / 2,
        ...viewRef.current,
      };
    }
  };

  const onPointerMove = (e) => {
    const prev = drag.current.get(e.pointerId);
    if (!prev) return;
    drag.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (drag.current.size >= 2 && pinch.current) {
      const [a, b] = [...drag.current.values()];
      const p = pinch.current;
      const k = clampK(p.k * (Math.hypot(a.x - b.x, a.y - b.y) / p.d));
      const r = k / p.k;
      setView({ k, x: p.cx - (p.cx - p.x) * r, y: p.cy - (p.cy - p.y) * r });
    } else if (drag.current.size === 1 && viewRef.current.k > 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    }
  };

  const endDrag = (e) => {
    drag.current.delete(e.pointerId);
    if (drag.current.size < 2) pinch.current = null;
  };

  const step = (mult) => setView((v) => ({ ...v, k: clampK(v.k * mult) }));

  return (
    <div className="scrim" onClick={onClose} role="dialog" aria-modal="true" aria-label={`${name} directory page`}>
      <div className="viewer" onClick={(e) => e.stopPropagation()}>
        <div className="viewer-bar">
          <span className="mono viewer-ident">{ident}</span>
          <span className="viewer-name">{name}</span>
          <div className="viewer-tools">
            <button className="ibtn" onClick={() => step(1 / 1.4)} aria-label="Zoom out">−</button>
            <button className="ibtn" onClick={() => step(1.4)} aria-label="Zoom in">+</button>
            <button className="ibtn" onClick={() => setView({ k: 1, x: 0, y: 0 })} aria-label="Reset zoom">
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                <path d="M2.5 6.5h4v-4M2.8 6.2A5.5 5.5 0 1 1 2.5 9.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="ibtn ibtn-close" onClick={onClose} aria-label="Close directory page">×</button>
          </div>
        </div>
        <div
          className="viewer-frame"
          ref={frame}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={() => setView((v) => (v.k > 1 ? { k: 1, x: 0, y: 0 } : { k: 2.5, x: 0, y: 0 }))}
          style={{ cursor: view.k > 1 ? 'grab' : 'zoom-in' }}
        >
          <img
            src={`${PATH}/images/${ident}.png`}
            alt={`State airport directory page for ${name}`}
            draggable="false"
            style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AboutModal({ onClose }) {
  return (
    <div className="scrim" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="about-title">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <button className="ibtn ibtn-close sheet-close" onClick={onClose} aria-label="Close">×</button>
        <p className="stencil eyebrow">About</p>
        <h2 id="about-title" className="sheet-title">Airstrip Map</h2>
        <div className="prose">
          <p>
            State airport directories list things no national database does — a courtesy car, bikes
            in the shed, a place to pitch a tent, a diner within walking distance. They are published
            as large PDFs that nobody reads. This map pulls them apart and puts them where you plan.
          </p>
          <p>
            Coverage follows what each state publishes, so it is uneven. Turn on{' '}
            <strong>All US airports</strong> to see every field, with links out to AirNav, SkyVector,
            and FAA diagrams even where no directory page exists.
          </p>
          <p>
            Data may be wrong or out of date. Verify against official sources before you fly.
          </p>
        </div>
        <div className="sheet-actions">
          <a className="btn btn-amber" href="https://buymeacoffee.com/noahpotti" target="_blank" rel="noopener noreferrer">
            Buy me a coffee
          </a>
          <a className="btn" href="https://github.com/thesubtlety/airstripmap" target="_blank" rel="noopener noreferrer">
            View source
          </a>
          <a className="btn" href="https://pirep.io" target="_blank" rel="noopener noreferrer">
            PiRep.io
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Airport card — a Chart Supplement entry, not a map bubble.
 * ------------------------------------------------------------------ */

function AirportCard({ airport, origin, diagrams, onClose, onSetOrigin, onOpenScan }) {
  const [scanState, setScanState] = useState('loading');
  const ident = airport.id;
  const isDirectory = airport.source === 'directory';

  useEffect(() => setScanState(isDirectory ? 'loading' : 'none'), [ident, isDirectory]);

  const diagram = faaDiagramUrl(ident, diagrams);
  const dist = origin ? nmBetween([origin.lat, origin.lon], [airport.latitude, airport.longitude]) : null;
  const has = AMENITIES.filter((a) => airport[a.key]);
  const elev = Number(airport.elevation);
  const hasElev = airport.elevation !== '' && airport.elevation != null && !isNaN(elev);

  return (
    <aside className="card" aria-label={`${airport.name} details`}>
      <header className="card-head">
        <div className="card-ident-row">
          <span className="mono card-ident">{ident}</span>
          <button className="ibtn ibtn-close" onClick={onClose} aria-label="Close airport details">×</button>
        </div>
        <h2 className="card-name">{airport.name}</h2>
      </header>

      <dl className="datagrid">
        {hasElev && (
          <div>
            <dt className="stencil">Elev</dt>
            <dd className="mono">{fmt(elev)}′</dd>
          </div>
        )}
        {dist != null && (
          <div>
            <dt className="stencil">Dist</dt>
            <dd className="mono">{dist < 10 ? dist.toFixed(1) : Math.round(dist)} nm</dd>
          </div>
        )}
        <div>
          <dt className="stencil">Lat / Lon</dt>
          <dd className="mono">
            {airport.latitude.toFixed(3)}, {airport.longitude.toFixed(3)}
          </dd>
        </div>
        {airport.type && (
          <div>
            <dt className="stencil">Type</dt>
            <dd className="cap">{airport.type.replace(/_/g, ' ')}</dd>
          </div>
        )}
      </dl>

      {isDirectory && (
        <div className="card-amenities">
          {has.length > 0 ? (
            has.map((a) => (
              <span key={a.key} className="tag">
                <Glyph name={a.key} />
                {a.label}
              </span>
            ))
          ) : (
            <p className="muted">No amenities listed in the state directory.</p>
          )}
        </div>
      )}

      {isDirectory && scanState !== 'error' && (
        <figure className="scan">
          <button
            className="scan-btn"
            onClick={() => onOpenScan(airport)}
            aria-label={`Open the full directory page for ${airport.name}`}
          >
            <img
              src={`${PATH}/images/${ident}.png`}
              alt={`Directory page for ${airport.name}`}
              onLoad={() => setScanState('ready')}
              onError={() => setScanState('error')}
            />
            <span className="scan-hint stencil">Open full page</span>
          </button>
          <figcaption className="stencil">State airport directory</figcaption>
        </figure>
      )}
      {isDirectory && scanState === 'error' && (
        <p className="muted scan-missing">No directory page published for this field.</p>
      )}

      <div className="card-links">
        <a className="btn" href={`https://www.airnav.com/airport/${ident}`} target="_blank" rel="noopener noreferrer">
          AirNav
        </a>
        <a className="btn" href={`https://www.skyvector.com/airport/${ident}`} target="_blank" rel="noopener noreferrer">
          SkyVector
        </a>
        {diagram && (
          <a className="btn" href={diagram} target="_blank" rel="noopener noreferrer">
            FAA diagram
          </a>
        )}
      </div>

      <button className="btn btn-wide" onClick={() => onSetOrigin(airport)}>
        Search from {ident}
      </button>
    </aside>
  );
}

/* ------------------------------------------------------------------ */

function MapBridge({ onReady, onZoom }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  }, [map, onReady]);
  useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  return null;
}

/* ------------------------------------------------------------------ */

export default function FullPageMap() {
  const [items, setItems] = useState([]);
  const [rawAll, setRawAll] = useState([]);
  const [diagrams, setDiagrams] = useState({});

  const [filters, setFilters] = useState({
    camping: false,
    courtesy_car: false,
    bicycles: false,
    meals: false,
  });
  const [showAll, setShowAll] = useState(true);
  const [baseLayer, setBaseLayer] = useState('street');

  const [origin, setOrigin] = useState(null); // { lat, lon, label }
  const [radiusNm, setRadiusNm] = useState(100);
  const [radiusLive, setRadiusLive] = useState(100);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const [selected, setSelected] = useState(null);
  const [scanFor, setScanFor] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [bucket, setBucket] = useState(0);
  const [hudOpen, setHudOpen] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= 768
  );

  const mapRef = useRef(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const handleMapReady = useCallback((map) => {
    mapRef.current = map;
    setBucket(zoomBucket(map.getZoom()));
  }, []);
  const handleZoom = useCallback((z) => setBucket(zoomBucket(z)), []);

  /* --- data --- */
  useEffect(() => {
    fetch(`${PATH}/data.json`)
      .then((r) => r.json())
      .then((d) =>
        setItems(
          d.items
            .map((it) => ({
              ...it,
              latitude: parseFloat(it.latitude),
              longitude: parseFloat(it.longitude),
              source: 'directory',
            }))
            .filter((it) => !isNaN(it.latitude) && !isNaN(it.longitude))
        )
      )
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    fetch(`${PATH}/airport_diagram_lookup.json`)
      .then((r) => r.json())
      .then(setDiagrams)
      .catch(() => setDiagrams({}));
  }, []);

  /* Fetched independently of data.json so the two requests overlap; the
     dedupe happens below once both have landed. */
  useEffect(() => {
    if (!showAll || rawAll.length) return;
    fetch(`${PATH}/airports_all.json`)
      .then((r) => r.json())
      .then(setRawAll)
      .catch(() => setRawAll([]));
  }, [showAll, rawAll.length]);

  const allAirports = useMemo(() => {
    if (!rawAll.length) return [];
    const known = new Set(items.map((i) => i.id));
    return rawAll
      .filter((a) => !known.has(a.id))
      .map((a) => ({
        id: a.id,
        name: a.name,
        latitude: a.lat,
        longitude: a.lon,
        type: a.type,
        source: 'base',
      }));
  }, [rawAll, items]);

  /* Sectional coverage starts at z8, so it rides on top of the street tiles
     rather than being the only layer. Zooming out past coverage reveals the
     base map instead of a black screen.

     Do NOT solve this with map.setMinZoom(). markercluster builds its distance
     grids from the map's minZoom when the group is added, but re-reads minZoom
     when each marker is inserted — and chunkedLoading makes that async. Moving
     the floor between those two moments leaves gridClusters[z] undefined and
     throws inside _addLayer. */
  useEffect(() => {
    const map = mapRef.current;
    if (map && baseLayer === 'sectional' && map.getZoom() < 8) map.setZoom(9);
  }, [baseLayer]);

  /* --- filtering --- */
  const inRadius = useCallback(
    (a) =>
      !origin ||
      radiusNm >= MAX_RADIUS_NM ||
      nmBetween([origin.lat, origin.lon], [a.latitude, a.longitude]) <= radiusNm,
    [origin, radiusNm]
  );

  const passesAmenities = useCallback(
    (it, f) => AMENITIES.every((a) => !f[a.key] || it[a.key]),
    []
  );

  const shown = useMemo(
    () => items.filter((it) => passesAmenities(it, filters) && inRadius(it)),
    [items, filters, passesAmenities, inRadius]
  );

  const shownBase = useMemo(
    () => (showAll ? allAirports.filter(inRadius) : []),
    [showAll, allAirports, inRadius]
  );

  /* Faceted counts: how many fields you'd get if you also turned this chip on. */
  const facets = useMemo(() => {
    const out = {};
    for (const a of AMENITIES) {
      const probe = { ...filters, [a.key]: true };
      out[a.key] = items.reduce(
        (n, it) => n + (passesAmenities(it, probe) && inRadius(it) ? 1 : 0),
        0
      );
    }
    return out;
  }, [items, filters, passesAmenities, inRadius]);

  const anyFilter = AMENITIES.some((a) => filters[a.key]);

  /* --- search --- */
  const pool = useMemo(
    () => (showAll && allAirports.length ? items.concat(allAirports) : items),
    [items, allAirports, showAll]
  );

  /* Built on first keystroke, not on load — indexing ~20k fields is a jank
     spike we don't need to pay before anyone has typed anything. */
  const fuseRef = useRef({ pool: null, fuse: null });
  const getFuse = () => {
    if (fuseRef.current.pool !== pool) {
      fuseRef.current = {
        pool,
        fuse: new Fuse(pool, {
          keys: [
            { name: 'id', weight: 2 },
            { name: 'name', weight: 1 },
          ],
          threshold: 0.3,
        }),
      };
    }
    return fuseRef.current.fuse;
  };

  const runSearch = (raw) => {
    const value = raw.toUpperCase();
    setQuery(value);
    setActiveIdx(-1);
    if (value.trim().length < 2) {
      setResults([]);
      setOpenList(false);
      return;
    }
    const found = getFuse().search(value).slice(0, 8).map((r) => r.item);
    setResults(found);
    setOpenList(found.length > 0);
  };

  const focusAirport = useCallback((a) => {
    setSelected(a);
    setOpenList(false);
    const map = mapRef.current;
    if (map) map.flyTo([a.latitude, a.longitude], Math.max(map.getZoom(), 11), { duration: 0.8 });
  }, []);

  const onSearchKeyDown = (e) => {
    if (!openList || !results.length) {
      if (e.key === 'Enter' && results[0]) focusAirport(results[0]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      focusAirport(results[activeIdx >= 0 ? activeIdx : 0]);
    } else if (e.key === 'Escape') {
      setOpenList(false);
    }
  };

  useEffect(() => {
    const away = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setOpenList(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  /* On a phone the card is a bottom sheet — leaving the control panel open
     behind it just stacks two overlays over the map. */
  useEffect(() => {
    if (selected && typeof window !== 'undefined' && window.innerWidth < 768) {
      setHudOpen(false);
    }
  }, [selected]);

  /* Escape unwinds the deepest layer first. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (scanFor) setScanFor(null);
      else if (aboutOpen) setAboutOpen(false);
      else if (openList) setOpenList(false);
      else if (selected) setSelected(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [scanFor, aboutOpen, openList, selected]);

  /* --- actions --- */
  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setOrigin({ lat: coords.latitude, lon: coords.longitude, label: 'your location' });
        if (mapRef.current) mapRef.current.flyTo([coords.latitude, coords.longitude], 9, { duration: 0.9 });
      },
      () => setOrigin(null)
    );
  };

  const originFromAirport = (a) => {
    setOrigin({ lat: a.latitude, lon: a.longitude, label: a.id });
    if (mapRef.current) mapRef.current.flyTo([a.latitude, a.longitude], 9, { duration: 0.8 });
  };

  const zoomBy = (d) => mapRef.current && mapRef.current.setZoom(mapRef.current.getZoom() + d);

  /* --- markers --- */
  const px = SYM_PX[bucket];
  const labelled = bucket >= LABEL_FROM_BUCKET;

  /* Memoised so selecting a field doesn't reconcile the whole marker set. */
  const directoryMarkers = useMemo(
    () =>
      shown.map((it) => (
        <Marker
          key={`d-${it.id}`}
          position={[it.latitude, it.longitude]}
          icon={airfieldIcon(directoryVariant(it), px, labelled ? it.id : null)}
          eventHandlers={{ click: () => setSelected(it) }}
        />
      )),
    [shown, px, labelled]
  );

  const baseMarkers = useMemo(
    () =>
      shownBase.map((a) => (
        <Marker
          key={`b-${a.id}`}
          position={[a.latitude, a.longitude]}
          icon={airfieldIcon(baseVariant(a), Math.round(px * 0.72), null)}
          eventHandlers={{ click: () => setSelected(a) }}
        />
      )),
    [shownBase, px]
  );

  const clusterIcon = (cluster, tone) => {
    const n = cluster.getChildCount();
    const size = n < 10 ? 30 : n < 100 ? 36 : 42;
    return L.divIcon({
      html: `<span class="mono">${n}</span>`,
      className: `cluster cluster-${tone}`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  const countLine = () => {
    if (!items.length) return 'Loading fields…';
    const where = origin && radiusNm < MAX_RADIUS_NM ? ` within ${radiusNm} nm of ${origin.label}` : '';
    return `${fmt(shown.length)} ${shown.length === 1 ? 'field' : 'fields'}${where}`;
  };

  return (
    <div className="app">
      {/* ---------------- HUD: top left ---------------- */}
      <div className="hud hud-left">
        <div className="bar">
          <button className="wordmark stencil" onClick={() => setAboutOpen(true)}>
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
              <path d={TICKS} stroke={C.amber} strokeWidth="2.4" strokeLinecap="round" fill="none" />
              <circle cx="12" cy="12" r="7" fill="none" stroke={C.amber} strokeWidth="2.4" />
            </svg>
            Airstrip Map
          </button>
          <button
            className="ibtn hud-toggle"
            onClick={() => setHudOpen((v) => !v)}
            aria-expanded={hudOpen}
            aria-controls="hud-panel"
            aria-label={hudOpen ? 'Hide controls' : 'Show controls'}
          >
            <Chevron dir={hudOpen ? 'up' : 'down'} />
          </button>
        </div>

        {hudOpen && (
          <div className="panel" id="hud-panel">
            <div className="search" ref={searchRef}>
              <svg className="search-icon" viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
                <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
                <path d="m10.6 10.6 3.4 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="search-input mono"
                placeholder="Ident or airport name"
                value={query}
                onChange={(e) => runSearch(e.target.value)}
                onKeyDown={onSearchKeyDown}
                onFocus={() => results.length && setOpenList(true)}
                role="combobox"
                aria-expanded={openList}
                aria-controls="search-list"
                aria-autocomplete="list"
                aria-activedescendant={activeIdx >= 0 ? `opt-${activeIdx}` : undefined}
                aria-label="Search airports by identifier or name"
              />
              {query && (
                <button
                  className="ibtn search-clear"
                  onClick={() => {
                    setQuery('');
                    setResults([]);
                    setOpenList(false);
                    inputRef.current?.focus();
                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
              {openList && (
                <ul className="listbox" id="search-list" role="listbox">
                  {results.map((r, i) => (
                    <li
                      key={`${r.source}-${r.id}`}
                      id={`opt-${i}`}
                      role="option"
                      aria-selected={i === activeIdx}
                      className={`option${i === activeIdx ? ' is-active' : ''}`}
                      onMouseEnter={() => setActiveIdx(i)}
                      onClick={() => focusAirport(r)}
                    >
                      <span className="mono option-id">{r.id}</span>
                      <span className="option-name">{r.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="chips" role="group" aria-label="Filter by amenity">
              {AMENITIES.map((a) => (
                <button
                  key={a.key}
                  className={`chip${filters[a.key] ? ' is-on' : ''}`}
                  aria-pressed={filters[a.key]}
                  onClick={() => setFilters((f) => ({ ...f, [a.key]: !f[a.key] }))}
                >
                  <Glyph name={a.key} />
                  {a.label}
                  <span className="chip-count mono">{fmt(facets[a.key] ?? 0)}</span>
                </button>
              ))}
            </div>

            {origin && (
              <div className="radius">
                <div className="radius-head">
                  <label className="stencil" htmlFor="radius-input">
                    Range from {origin.label}
                  </label>
                  <span className="mono radius-val">
                    {radiusLive >= MAX_RADIUS_NM ? 'any' : `${radiusLive} nm`}
                  </span>
                </div>
                <input
                  id="radius-input"
                  type="range"
                  className="slider"
                  min="25"
                  max={MAX_RADIUS_NM}
                  step="25"
                  value={radiusLive}
                  onChange={(e) => setRadiusLive(Number(e.target.value))}
                  onMouseUp={() => setRadiusNm(radiusLive)}
                  onTouchEnd={() => setRadiusNm(radiusLive)}
                  onKeyUp={() => setRadiusNm(radiusLive)}
                  onBlur={() => setRadiusNm(radiusLive)}
                />
                <button className="linkish" onClick={() => setOrigin(null)}>
                  Clear range ring
                </button>
              </div>
            )}

            <label className="switch">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              <span className="switch-track" aria-hidden="true"><span className="switch-knob" /></span>
              <span className="switch-label">
                All US airports
                <span className="switch-sub">16,682 fields · no directory pages</span>
              </span>
            </label>

            <p className={`readout mono${shown.length === 0 && items.length ? ' is-empty' : ''}`}>
              {shown.length === 0 && items.length ? 'No fields match — clear a filter or widen the range.' : countLine()}
            </p>
            {anyFilter && (
              <button
                className="linkish"
                onClick={() => setFilters({ camping: false, courtesy_car: false, bicycles: false, meals: false })}
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* ---------------- HUD: bottom right ---------------- */}
      <div className={`hud hud-right${selected ? ' is-shifted' : ''}`}>
        <div className="segmented" role="radiogroup" aria-label="Base map">
          {LAYERS.map((l) => (
            <button
              key={l.key}
              role="radio"
              aria-checked={baseLayer === l.key}
              className={`seg${baseLayer === l.key ? ' is-on' : ''}`}
              onClick={() => setBaseLayer(l.key)}
            >
              {l.label}
            </button>
          ))}
        </div>
        {/* Locate sits in its own bezel — it does something to the data
            (sets the range ring), the zoom buttons only move the view. */}
        <div className="stack">
          <button
            className={`mbtn${origin ? ' is-on' : ''}`}
            onClick={locate}
            title="Find my location"
            aria-label="Find my location"
          >
            <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
              <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="stack">
          <button className="mbtn" onClick={() => zoomBy(1)} title="Zoom in" aria-label="Zoom in">+</button>
          <button className="mbtn" onClick={() => zoomBy(-1)} title="Zoom out" aria-label="Zoom out">−</button>
        </div>
      </div>

      {/* ---------------- Legend ---------------- */}
      <div className="legend">
        <span className="legend-item">
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" dangerouslySetInnerHTML={{ __html: symbolSVG('amenity') }} />
          has amenities
        </span>
        <span className="legend-item">
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" dangerouslySetInnerHTML={{ __html: symbolSVG('directory') }} />
          in a state directory
        </span>
        {showAll && (
          <span className="legend-item">
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" dangerouslySetInnerHTML={{ __html: symbolSVG('small') }} />
            all US fields
          </span>
        )}
      </div>

      {/* ---------------- Map ---------------- */}
      <MapContainer className="map" center={[39.8283, -98.5795]} zoom={5} zoomControl={false} attributionControl>
        <MapBridge onReady={handleMapReady} onZoom={handleZoom} />

        {baseLayer !== 'topo' && (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            zIndex={1}
          />
        )}
        {baseLayer === 'topo' && (
          <TileLayer
            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, SRTM &middot; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
            maxZoom={17}
            zIndex={1}
          />
        )}
        {baseLayer === 'sectional' && (
          <TileLayer
            url="https://tiles.arcgis.com/tiles/ssFJjBXIUyZDrSYZ/arcgis/rest/services/VFR_Sectional/MapServer/tile/{z}/{y}/{x}"
            attribution='VFR sectional charts &middot; <a href="https://www.faa.gov/air_traffic/flight_info/aeronav/">FAA</a>'
            minZoom={8}
            maxZoom={18}
            minNativeZoom={8}
            maxNativeZoom={12}
            updateWhenZooming={false}
            keepBuffer={2}
            zIndex={2}
          />
        )}

        {origin && (
          <>
            <Marker position={[origin.lat, origin.lon]} icon={originIcon} interactive={false} />
            {radiusNm < MAX_RADIUS_NM && (
              <Circle
                center={[origin.lat, origin.lon]}
                radius={radiusNm * METERS_PER_NM}
                interactive={false}
                pathOptions={{
                  color: C.amber,
                  weight: 1.4,
                  opacity: 0.85,
                  dashArray: '6 7',
                  fillColor: C.amber,
                  fillOpacity: 0.05,
                }}
              />
            )}
          </>
        )}

        {showAll && baseMarkers.length > 0 && (
          <MarkerClusterGroup
            chunkedLoading
            showCoverageOnHover={false}
            disableClusteringAtZoom={11}
            maxClusterRadius={38}
            iconCreateFunction={(c) => clusterIcon(c, 'base')}
          >
            {baseMarkers}
          </MarkerClusterGroup>
        )}

        {/* No baseLayer-keyed remount: the zoom bounds no longer change with
            the layer, and rebuilding these groups would re-cluster 16k markers
            on every switch. */}
        <MarkerClusterGroup
          chunkedLoading
          showCoverageOnHover={false}
          spiderfyOnEveryZoom
          disableClusteringAtZoom={9}
          maxClusterRadius={50}
          iconCreateFunction={(c) => clusterIcon(c, 'dir')}
        >
          {directoryMarkers}
        </MarkerClusterGroup>

        {selected && (
          <Marker
            position={[selected.latitude, selected.longitude]}
            icon={selectionIcon}
            interactive={false}
            zIndexOffset={900}
          />
        )}
      </MapContainer>

      {selected && (
        <AirportCard
          key={selected.id}
          airport={selected}
          origin={origin}
          diagrams={diagrams}
          onClose={() => setSelected(null)}
          onSetOrigin={originFromAirport}
          onOpenScan={setScanFor}
        />
      )}

      {scanFor && (
        <ScanViewer ident={scanFor.id} name={scanFor.name} onClose={() => setScanFor(null)} />
      )}
      {aboutOpen && <AboutModal onClose={() => setAboutOpen(false)} />}
    </div>
  );
}
